import clsx from 'clsx';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import { useSharedProgressBarPropsContext } from './hooks/useProgressBarPropsContext.js';
import { value } from './ProgressBarValue.sty.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { textStyle } from '../../styles/textStyle.sty.js';

/**
 * Accepted properties for ProgressBarValue.
 * @public
 * */
export interface ProgressBarValueProps
  extends WithChildren,
    StylingProps,
    DataTestId {}

/**
 * ProgressBarValue sub-component is rendered inside ProgressBar component.
 * @public
 */
export const ProgressBarValue = forwardRef<
  HTMLDivElement,
  ProgressBarValueProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: ProgressBarValueProps = props;
  const intl = useIntl();

  const progressBarDefaultLabel = intl.formatMessage({
    defaultMessage: 'Loading',
    id: '3J6qHQEUwDxvZuIl',
    description: 'Default label for the progress-bar.',
  });

  const { density } = useSharedProgressBarPropsContext();

  const childIsText = canReceiveDefaultTextFormat(children);

  return (
    <div
      data-testid={dataTestId}
      ref={forwardedRef}
      className={clsx(
        consumerClassName,
        value({ size: density, childIsText }),
        textStyle({ textStyle: 'small' }),
      )}
      style={consumerStyle}
      {...remainingProps}
    >
      {children || progressBarDefaultLabel}
    </div>
  );
});

(
  ProgressBarValue as typeof ProgressBarValue & { displayName: string }
).displayName = 'ProgressBar.Value';
